import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Building2, FolderKanban, Inbox, LayoutDashboard, LogOut, Pencil, Plus, Trash2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import { CITIES, PROPERTY_TYPES, PURPOSE_LABELS, formatPrice } from "@/lib/format";

const TABS = [
  { id: "overview", label: "نظرة عامة", icon: LayoutDashboard },
  { id: "properties", label: "العقارات", icon: Building2 },
  { id: "projects", label: "المشاريع", icon: FolderKanban },
  { id: "requests", label: "الطلبات", icon: Inbox },
];

const STATUS_LABELS = { new: "جديد", contacted: "تم التواصل", closed: "مغلق" };

const emptyProperty = { title: "", description: "", city: "جدة", district: "", purpose: "sale", type: "فيلا", price: "", area: "", bedrooms: 0, bathrooms: 0, images: "", amenities: "", featured: false, project_id: "" };
const emptyProject = { name: "", description: "", city: "جدة", location: "", status: "قيد الإنشاء", images: "", unit_types: "", starting_price: "", features: "" };

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-heading font-semibold text-forest">{label}</label>
      {children}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [properties, setProperties] = useState([]);
  const [projects, setProjects] = useState([]);
  const [requests, setRequests] = useState([]);
  const [modal, setModal] = useState(null); // {kind:'property'|'project', id?:string, form:{}}
  const [busy, setBusy] = useState(false);

  const loadAll = async () => {
    try {
      const [s, p, pr, rq] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/properties"),
        api.get("/projects"),
        api.get("/admin/requests"),
      ]);
      setStats(s.data);
      setProperties(p.data.items);
      setProjects(pr.data.items);
      setRequests(rq.data.items);
    } catch (e) {
      toast.error(apiError(e, "تعذر تحميل البيانات"));
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const openCreate = (kind) => setModal({ kind, id: null, form: kind === "property" ? emptyProperty : emptyProject });

  const openEdit = (kind, item) => {
    if (kind === "property") {
      setModal({
        kind, id: item.id,
        form: { ...item, images: (item.images || []).join("\n"), amenities: (item.amenities || []).join("، "), project_id: item.project_id || "" },
      });
    } else {
      setModal({
        kind, id: item.id,
        form: { ...item, images: (item.images || []).join("\n"), unit_types: (item.unit_types || []).join("، "), features: (item.features || []).join("، "), starting_price: item.starting_price ?? "" },
      });
    }
  };

  const saveModal = async () => {
    setBusy(true);
    const { kind, id, form } = modal;
    try {
      if (kind === "property") {
        const payload = {
          ...form,
          price: Number(form.price) || 0,
          area: Number(form.area) || 0,
          bedrooms: Number(form.bedrooms) || 0,
          bathrooms: Number(form.bathrooms) || 0,
          images: form.images.split("\n").map((s) => s.trim()).filter(Boolean),
          amenities: form.amenities.split(/[،,]/).map((s) => s.trim()).filter(Boolean),
          project_id: form.project_id || null,
        };
        if (id) await api.put(`/admin/properties/${id}`, payload);
        else await api.post("/admin/properties", payload);
        toast.success(id ? "تم تحديث العقار" : "تمت إضافة العقار");
      } else {
        const payload = {
          ...form,
          starting_price: form.starting_price === "" ? null : Number(form.starting_price),
          images: form.images.split("\n").map((s) => s.trim()).filter(Boolean),
          unit_types: form.unit_types.split(/[،,]/).map((s) => s.trim()).filter(Boolean),
          features: form.features.split(/[،,]/).map((s) => s.trim()).filter(Boolean),
        };
        if (id) await api.put(`/admin/projects/${id}`, payload);
        else await api.post("/admin/projects", payload);
        toast.success(id ? "تم تحديث المشروع" : "تمت إضافة المشروع");
      }
      setModal(null);
      await loadAll();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (kind, id) => {
    if (!window.confirm("تأكيد الحذف؟")) return;
    try {
      await api.delete(`/admin/${kind === "property" ? "properties" : "projects"}/${id}`);
      toast.success("تم الحذف");
      await loadAll();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setRequestStatus = async (id, status) => {
    try {
      await api.patch(`/admin/requests/${id}`, { status });
      setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success("تم تحديث الحالة");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const statCards = useMemo(
    () =>
      stats
        ? [
            { label: "عقار معروض", value: stats.properties, icon: Building2 },
            { label: "مشروع", value: stats.projects, icon: FolderKanban },
            { label: "إجمالي الطلبات", value: stats.requests, icon: Inbox },
            { label: "طلبات جديدة", value: stats.requests_new, icon: Inbox },
          ]
        : [],
    [stats]
  );

  return (
    <div className="min-h-screen bg-ivory" data-testid="admin-dashboard">
      <Seo title="لوحة التحكم" />
      <header className="sticky top-0 z-40 bg-forest">
        <div className="container-lux flex items-center justify-between py-4">
          <Link to="/"><Logo dark /></Link>
          <div className="flex items-center gap-4">
            <span className="hidden md:block text-sm text-ivory/70" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={logout} className="flex items-center gap-2 rounded-full border border-ivory/25 px-4 py-2 text-sm text-ivory hover:bg-ivory/10 transition-colors" data-testid="admin-logout-button">
              <LogOut className="h-4 w-4" aria-hidden="true" />
              خروج
            </button>
          </div>
        </div>
      </header>

      <div className="bg-gold/15 border-b border-gold/30">
        <p className="container-lux py-2.5 text-xs text-charcoal/80" data-testid="admin-demo-disclaimer">
          لوحة تحكم النموذج التجريبي — جميع البيانات المعروضة افتراضية لأغراض العرض.
        </p>
      </div>

      <div className="container-lux py-8 space-y-8">
        <nav className="flex flex-wrap gap-2" aria-label="أقسام لوحة التحكم">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-heading font-semibold transition-colors ${
                tab === t.id ? "bg-forest text-ivory" : "bg-white text-forest border border-forest/15 hover:border-gold"
              }`}
            >
              <t.icon className="h-4 w-4" aria-hidden="true" />
              {t.label}
            </button>
          ))}
        </nav>

        {tab === "overview" && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="admin-stats">
            {statCards.map((s) => (
              <div key={s.label} className="card-lux rounded-2xl p-6">
                <s.icon className="h-6 w-6 text-gold-dark mb-3" aria-hidden="true" />
                <p className="font-heading font-extrabold text-3xl text-forest">{s.value}</p>
                <p className="text-sm text-muted-foreground mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "properties" && (
          <div className="space-y-5" data-testid="admin-properties-tab">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-xl text-forest">العقارات ({properties.length})</h2>
              <button onClick={() => openCreate("property")} className="btn-gold !py-2.5 text-sm" data-testid="admin-add-property">
                <Plus className="h-4 w-4" aria-hidden="true" />
                إضافة عقار
              </button>
            </div>
            <div className="overflow-x-auto rounded-2xl bg-white shadow-[0_10px_40px_rgba(10,42,26,0.05)]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-forest/10 text-forest font-heading">
                    <th className="px-4 py-3 text-start">العقار</th>
                    <th className="px-4 py-3 text-start">المدينة</th>
                    <th className="px-4 py-3 text-start">النوع</th>
                    <th className="px-4 py-3 text-start">الغرض</th>
                    <th className="px-4 py-3 text-start">السعر</th>
                    <th className="px-4 py-3 text-start">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {properties.map((p) => (
                    <tr key={p.id} className="border-b border-forest/5 hover:bg-ivory/60" data-testid={`admin-property-row-${p.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <img src={p.images?.[0]} alt="" className="h-10 w-14 rounded-lg object-cover" loading="lazy" />
                          <span className="font-semibold text-charcoal line-clamp-1">{p.title}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">{p.city}</td>
                      <td className="px-4 py-3">{p.type}</td>
                      <td className="px-4 py-3">{PURPOSE_LABELS[p.purpose]}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{formatPrice(p.price, p.purpose)}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => openEdit("property", p)} aria-label="تعديل" className="rounded-lg border border-forest/15 p-2 text-forest hover:border-gold" data-testid={`admin-edit-property-${p.id}`}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button onClick={() => remove("property", p.id)} aria-label="حذف" className="rounded-lg border border-destructive/30 p-2 text-destructive hover:bg-destructive/10" data-testid={`admin-delete-property-${p.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "projects" && (
          <div className="space-y-5" data-testid="admin-projects-tab">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-xl text-forest">المشاريع ({projects.length})</h2>
              <button onClick={() => openCreate("project")} className="btn-gold !py-2.5 text-sm" data-testid="admin-add-project">
                <Plus className="h-4 w-4" aria-hidden="true" />
                إضافة مشروع
              </button>
            </div>
            <div className="overflow-x-auto rounded-2xl bg-white shadow-[0_10px_40px_rgba(10,42,26,0.05)]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-forest/10 text-forest font-heading">
                    <th className="px-4 py-3 text-start">المشروع</th>
                    <th className="px-4 py-3 text-start">المدينة</th>
                    <th className="px-4 py-3 text-start">الحالة</th>
                    <th className="px-4 py-3 text-start">الوحدات</th>
                    <th className="px-4 py-3 text-start">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((pr) => (
                    <tr key={pr.id} className="border-b border-forest/5 hover:bg-ivory/60" data-testid={`admin-project-row-${pr.id}`}>
                      <td className="px-4 py-3 font-semibold text-charcoal">{pr.name}</td>
                      <td className="px-4 py-3">{pr.city}</td>
                      <td className="px-4 py-3">{pr.status}</td>
                      <td className="px-4 py-3">{pr.unit_types?.join("، ")}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => openEdit("project", pr)} aria-label="تعديل" className="rounded-lg border border-forest/15 p-2 text-forest hover:border-gold" data-testid={`admin-edit-project-${pr.id}`}>
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button onClick={() => remove("project", pr.id)} aria-label="حذف" className="rounded-lg border border-destructive/30 p-2 text-destructive hover:bg-destructive/10" data-testid={`admin-delete-project-${pr.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "requests" && (
          <div className="space-y-5" data-testid="admin-requests-tab">
            <h2 className="font-heading font-bold text-xl text-forest">طلبات العقارات ({requests.length})</h2>
            {requests.length === 0 ? (
              <p className="rounded-2xl bg-white p-10 text-center text-sm text-muted-foreground shadow-[0_10px_40px_rgba(10,42,26,0.05)]" data-testid="admin-requests-empty">
                لا توجد طلبات بعد.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-2xl bg-white shadow-[0_10px_40px_rgba(10,42,26,0.05)]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-forest/10 text-forest font-heading">
                      <th className="px-4 py-3 text-start">الاسم</th>
                      <th className="px-4 py-3 text-start">الجوال</th>
                      <th className="px-4 py-3 text-start">الغرض</th>
                      <th className="px-4 py-3 text-start">المدينة / النوع</th>
                      <th className="px-4 py-3 text-start">الميزانية</th>
                      <th className="px-4 py-3 text-start">الحالة</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((r) => (
                      <tr key={r.id} className="border-b border-forest/5 hover:bg-ivory/60" data-testid={`admin-request-row-${r.id}`}>
                        <td className="px-4 py-3 font-semibold">{r.name}</td>
                        <td className="px-4 py-3" dir="ltr">{r.phone}</td>
                        <td className="px-4 py-3">{r.purpose === "buy" ? "شراء" : "إيجار"}</td>
                        <td className="px-4 py-3">{r.city}{r.ptype ? ` / ${r.ptype}` : ""}</td>
                        <td className="px-4 py-3">{r.budget || "—"}</td>
                        <td className="px-4 py-3">
                          <select value={r.status} onChange={(e) => setRequestStatus(r.id, e.target.value)} className="input-lux !py-1.5 !text-xs" aria-label="حالة الطلب" data-testid={`admin-request-status-${r.id}`}>
                            {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-forest/60 p-4 backdrop-blur-sm" data-testid="admin-modal">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white p-8 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-xl text-forest">
                {modal.id ? "تعديل" : "إضافة"} {modal.kind === "property" ? "عقار" : "مشروع"}
              </h3>
              <button onClick={() => setModal(null)} aria-label="إغلاق" className="rounded-full p-2 hover:bg-forest/5" data-testid="admin-modal-close">
                <X className="h-5 w-5" />
              </button>
            </div>

            {modal.kind === "property" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="العنوان *"><input className="input-lux" value={modal.form.title} onChange={(e) => setModal({ ...modal, form: { ...modal.form, title: e.target.value } })} data-testid="form-property-title" /></Field>
                <Field label="الحي"><input className="input-lux" value={modal.form.district} onChange={(e) => setModal({ ...modal, form: { ...modal.form, district: e.target.value } })} /></Field>
                <Field label="المدينة">
                  <select className="input-lux" value={modal.form.city} onChange={(e) => setModal({ ...modal, form: { ...modal.form, city: e.target.value } })}>
                    {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="الغرض">
                  <select className="input-lux" value={modal.form.purpose} onChange={(e) => setModal({ ...modal, form: { ...modal.form, purpose: e.target.value } })}>
                    <option value="sale">للبيع</option>
                    <option value="rent">للإيجار</option>
                  </select>
                </Field>
                <Field label="النوع">
                  <select className="input-lux" value={modal.form.type} onChange={(e) => setModal({ ...modal, form: { ...modal.form, type: e.target.value } })}>
                    {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="السعر (ر.س) *"><input type="number" className="input-lux" value={modal.form.price} onChange={(e) => setModal({ ...modal, form: { ...modal.form, price: e.target.value } })} data-testid="form-property-price" /></Field>
                <Field label="المساحة (م²)"><input type="number" className="input-lux" value={modal.form.area} onChange={(e) => setModal({ ...modal, form: { ...modal.form, area: e.target.value } })} /></Field>
                <Field label="غرف النوم"><input type="number" className="input-lux" value={modal.form.bedrooms} onChange={(e) => setModal({ ...modal, form: { ...modal.form, bedrooms: e.target.value } })} /></Field>
                <Field label="دورات المياه"><input type="number" className="input-lux" value={modal.form.bathrooms} onChange={(e) => setModal({ ...modal, form: { ...modal.form, bathrooms: e.target.value } })} /></Field>
                <Field label="المشروع المرتبط">
                  <select className="input-lux" value={modal.form.project_id} onChange={(e) => setModal({ ...modal, form: { ...modal.form, project_id: e.target.value } })}>
                    <option value="">بدون مشروع</option>
                    {projects.map((pr) => <option key={pr.id} value={pr.id}>{pr.name}</option>)}
                  </select>
                </Field>
                <div className="md:col-span-2 space-y-4">
                  <Field label="روابط الصور (رابط في كل سطر)"><textarea rows={3} className="input-lux resize-none" dir="ltr" value={modal.form.images} onChange={(e) => setModal({ ...modal, form: { ...modal.form, images: e.target.value } })} data-testid="form-property-images" /></Field>
                  <Field label="المميزات (افصل بفاصلة)"><input className="input-lux" value={modal.form.amenities} onChange={(e) => setModal({ ...modal, form: { ...modal.form, amenities: e.target.value } })} /></Field>
                  <Field label="الوصف"><textarea rows={3} className="input-lux resize-none" value={modal.form.description} onChange={(e) => setModal({ ...modal, form: { ...modal.form, description: e.target.value } })} /></Field>
                  <label className="flex items-center gap-2 text-sm font-heading font-semibold text-forest">
                    <input type="checkbox" checked={modal.form.featured} onChange={(e) => setModal({ ...modal, form: { ...modal.form, featured: e.target.checked } })} className="h-4 w-4 accent-[#C5A869]" data-testid="form-property-featured" />
                    عقار مميز (يظهر في الرئيسية)
                  </label>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="اسم المشروع *"><input className="input-lux" value={modal.form.name} onChange={(e) => setModal({ ...modal, form: { ...modal.form, name: e.target.value } })} data-testid="form-project-name" /></Field>
                <Field label="الموقع"><input className="input-lux" value={modal.form.location} onChange={(e) => setModal({ ...modal, form: { ...modal.form, location: e.target.value } })} /></Field>
                <Field label="المدينة">
                  <select className="input-lux" value={modal.form.city} onChange={(e) => setModal({ ...modal, form: { ...modal.form, city: e.target.value } })}>
                    {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="الحالة">
                  <select className="input-lux" value={modal.form.status} onChange={(e) => setModal({ ...modal, form: { ...modal.form, status: e.target.value } })}>
                    <option>قيد الإنشاء</option>
                    <option>جاهز قريباً</option>
                    <option>جاهز</option>
                  </select>
                </Field>
                <Field label="السعر يبدأ من (ر.س)"><input type="number" className="input-lux" value={modal.form.starting_price} onChange={(e) => setModal({ ...modal, form: { ...modal.form, starting_price: e.target.value } })} /></Field>
                <Field label="أنواع الوحدات (افصل بفاصلة)"><input className="input-lux" value={modal.form.unit_types} onChange={(e) => setModal({ ...modal, form: { ...modal.form, unit_types: e.target.value } })} /></Field>
                <div className="md:col-span-2 space-y-4">
                  <Field label="روابط الصور (رابط في كل سطر)"><textarea rows={3} className="input-lux resize-none" dir="ltr" value={modal.form.images} onChange={(e) => setModal({ ...modal, form: { ...modal.form, images: e.target.value } })} /></Field>
                  <Field label="المرافق (افصل بفاصلة)"><input className="input-lux" value={modal.form.features} onChange={(e) => setModal({ ...modal, form: { ...modal.form, features: e.target.value } })} /></Field>
                  <Field label="الوصف"><textarea rows={3} className="input-lux resize-none" value={modal.form.description} onChange={(e) => setModal({ ...modal, form: { ...modal.form, description: e.target.value } })} /></Field>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button onClick={saveModal} disabled={busy} className="btn-gold flex-1 disabled:opacity-60" data-testid="admin-modal-save">
                {busy ? "جارٍ الحفظ…" : "حفظ"}
              </button>
              <button onClick={() => setModal(null)} className="btn-outline-forest" data-testid="admin-modal-cancel">إلغاء</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
